/* eslint-disable react-refresh/only-export-components */
/**
 * @file src/i18n.tsx
 * @fileoverview Provides internationalization (i18n) functionality for the application.
 * It sets up the I18nContext and I18nProvider to manage language locale state (English/Dutch)
 * and translation lookups. Translations are loaded from JSON files in the `locales` directory.
 * The `useI18n` hook allows components to access the current locale and translation function.
 * It also synchronizes the `<html>` lang attribute with the current locale.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { translate, type Locale } from './utils/translate';
export type { Locale } from './utils/translate';

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale, options?: { updateUrl?: boolean }) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const pathLocale = window.location.pathname.split('/')[1];
  const initialLocale: Locale =
    pathLocale === 'en' || pathLocale === 'nl'
      ? (pathLocale as Locale)
      : 'en';
  // Ensure the html lang attribute is set before the first render
  document.documentElement.lang = initialLocale;
  const [localeState, setLocaleState] = useState<Locale>(initialLocale);

  // Sync the html lang attribute with the current locale
  React.useEffect(() => {
    document.documentElement.lang = localeState;
  }, [localeState]);

  const setLocale = useCallback((newLocale: Locale, { updateUrl = true } = {}) => {
    setLocaleState(newLocale);
    if (!updateUrl) return;
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'en' || parts[0] === 'nl') {
      parts[0] = newLocale;
    } else {
      parts.unshift(newLocale);
    }
    const newPath = `/${parts.join('/')}`;
    navigate(newPath + window.location.search + window.location.hash, { replace: true });
  }, [navigate]);

  const t = (key: string, params?: Record<string, string | number>) => {
    return translate(localeState, key, params);
  };

  return (
    <I18nContext.Provider value={{ locale: localeState, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within an I18nProvider');
  return context;
};
